import { useEffect, useState } from 'react';
import { WidgetProps } from '@rjsf/utils';
import styled from '@emotion/styled';
import { Button, Grid, Typography } from '@mui/material';

import { PolarisIcon } from '@/exports/columnCommonComponents';
import TerritoriesSelectionModal from '@/components/_common/SchemaForm/Widgets/TerritoriesWidget/TerritoriesSelectionModal';
import TerritoriesList from './TerritoryList';

export type TerritoryState = {
  territory: string;
  download_availability: boolean;
};

export type WindowTerritory = { enum: string[]; title: string };

const TerritoriesWidget = (props: WidgetProps) => {
  const {
    value,
    registry,
    onChange,
    formContext: { formData },
    readonly
  } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTerritories, setSelectedTerritories] = useState<TerritoryState[]>(value || []);

  useEffect(() => {
    if (value?.length) {
      const newSelectedTerritories = value.filter((v: TerritoryState) => v.territory);
      setSelectedTerritories(newSelectedTerritories);
      onChange(newSelectedTerritories);
    }
  }, [value]);

  const handleAddNewClick = () => {
    setIsModalOpen(true);
  };

  const handleModalSubmit = () => {
    onChange(selectedTerritories);
    setIsModalOpen(false);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleSelectedTerritoriesUpdate = (newSelectedTerritories: TerritoryState[]) => {
    setSelectedTerritories(newSelectedTerritories);
  };

  const handleDeleteSelectedTerritory = (territory: string | string[]) => {
    if (typeof territory === 'string') {
      setSelectedTerritories((prev) => {
        const newSelectedTerritories = prev.filter((st) => st.territory !== territory);
        onChange(newSelectedTerritories);
        return newSelectedTerritories;
      });
    } else {
      setSelectedTerritories((prev) => {
        const newSelectedTerritories = prev.filter((st) => !territory.includes(st.territory));
        onChange(newSelectedTerritories);
        return newSelectedTerritories;
      });
    }
  };

  const handleDownloadChange = (territory: string, checked: boolean) => {
    setSelectedTerritories((prev) => {
      const newSelectedTerritories = prev.map((st) => {
        if (st.territory === territory) {
          return { ...st, download_availability: checked };
        }
        return st;
      });
      onChange(newSelectedTerritories);
      return newSelectedTerritories;
    });
  };

  return (
    <Grid container flexDirection="column" alignItems="flex-start" gap={2}>
      <Typography variant="h5" color="primary">
        Territory
      </Typography>
      {value.length > 0 && (
        <Content>
          <TerritoriesList
            valuesOnly
            registry={registry}
            selectedTerritories={value}
            onSelectedTerritoriesUpdate={handleSelectedTerritoriesUpdate}
            formData={formData}
            handleDeleteSelectedTerritory={handleDeleteSelectedTerritory}
            handleDownloadChange={handleDownloadChange}
            readOnly={!!readonly}
            schema={registry.rootSchema}
          />
        </Content>
      )}
      {!readonly && (
        <>
          <Button variant="outlined" startIcon={<PolarisIcon icon="add" height={16} />} onClick={handleAddNewClick}>
            Add New
          </Button>
          <TerritoriesSelectionModal
            isOpen={isModalOpen}
            selectedTerritories={selectedTerritories}
            value={value}
            registry={registry}
            onSubmit={handleModalSubmit}
            onClose={handleModalClose}
            onSelectedTerritoriesUpdate={handleSelectedTerritoriesUpdate}
            formData={formData}
          />
        </>
      )}
    </Grid>
  );
};

export default TerritoriesWidget;

const Content = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '786px'
}));
